import { useCallback, useContext, useEffect, useRef, useState } from "react"
import styled from "styled-components"
import { useLocale, useT } from "react-polyglot-hooks"
import DefaultLayout from "components/layouts/DefaultLayout"
import Typography from "components/styled/Typography"
import { SectionContainer } from "components/styled/Grid"
import { useRouter } from "next/router"
import { useDispatch } from "react-redux"
import castArray from "lodash/castArray"
import isEmpty from "lodash/isEmpty"
import isEqual from "lodash/isEqual"
import { updateCarsFilter } from "redux/actions"
import dynamic from "next/dynamic"
import { sendEvent } from "scripts/GoogleAnalytics"
import RentingSeo from "components/SEO/RentingSeo"
import getQueryFilters from "helpers/getQueryFilters"
import useMediaQuery from "hooks/useMediaQuery"
import tm from "theme"
import { mq } from "helpers/styled"
import useFilterPage from "hooks/useFilterPage"
import MainMenuCollection from "components/structuredData/MainMenuCollection"
import Actions from "./Actions"
import Stock from "./Stock"
import { RentingContext } from "./RentingContext"
import ListLandings from "./ListLandings"
import DynamicSeoSection from "../../components/common/DynamicSeoSection"
import { SeoPageContext } from "../SeoPageScene/SeoPageContext"

const Filter = dynamic(() => import("./Filter"))
const PreOwnedBanner = dynamic(() => import("components/PreOwnedBanner"))
const PreOwned = dynamic(() => import("./PreOwned"))

export const Titles = styled.div`
  text-align: center;
  margin-bottom: 2rem;
`

export const Title = styled(Typography)`
  margin-top: 15px;
  margin-bottom: 15px;
`

const StyledRow = styled.div`
  margin: 0;
  display: flex;
  justify-content: space-between;
`

const StyledCol = styled.div`
  padding: 0;
  width: 100%;

  .with-filter & {
    @media ${mq("lg")} {
      margin-left: auto;
      width: calc(100% - 250px - 7px);
    }

    @media ${mq("xl")} {
      width: calc(100% - 250px - 20px);
    }
  }
`

export const ListWrapper = styled.div`
  margin: -15px 0 0;

  @media ${mq("md")} {
    margin-top: 0;
    padding: 25px 0 50px;
  }
`

function RentingScene() {
  const { variant, page, cars, preOwnedCars, footerLinks, landingsList, initialFilter, disableUrlFilters } = useContext(RentingContext)
  const { seoData } = useContext(SeoPageContext)

  const hasFilter = variant !== "commercial"

  const [show, setShow] = useState(hasFilter ? "desktop" : null)
  const [hasCars, setHasCars] = useState({ stock: true, preOwned: true })
  const filterUpdated = useRef(false)
  const initialFilterApplied = useRef(false)
  const prevQuery = useRef({})
  const t = useT()
  const locale = useLocale()
  const dispatch = useDispatch()
  const { query } = useRouter()
  const isDesktop = useMediaQuery(tm.breakpoints.lg)
  const [hasQuery, setHasQuery] = useState(false)

  useFilterPage(hasFilter)

  const onFilterUpdate = () => {
    filterUpdated.current = true
  }

  useEffect(() => {
    if (filterUpdated.current) return

    if (!disableUrlFilters) {
      const parsedQuery = getQueryFilters(query)

      if (parsedQuery.duration) parsedQuery.duration = castArray(parsedQuery.duration).map(v => +v)
      if (parsedQuery.kilometers) parsedQuery.kilometers = castArray(parsedQuery.kilometers).map(v => +v)

      if (!isEmpty(parsedQuery)) {
        if (isEqual(prevQuery.current, parsedQuery)) return

        prevQuery.current = parsedQuery
        initialFilterApplied.current = true

        sendEvent("Filter", { event_category: variant, event_label: "Filter from url" })
        dispatch(updateCarsFilter(parsedQuery))
        return
      }
    }

    if (initialFilterApplied.current) return

    if (!isEmpty(initialFilter)) {
      initialFilterApplied.current = true
      dispatch(updateCarsFilter(initialFilter))
    }
  }, [disableUrlFilters, dispatch, initialFilter, query, variant])

  const setHasStock = useCallback(val => setHasCars(c => ({ ...c, stock: val })), [])
  const setHasPreOwned = useCallback(val => setHasCars(c => ({ ...c, preOwned: val })), [])

  useEffect(() => {
    setHasQuery(!isEmpty(window.location.search))
  })


  return (
    <DefaultLayout page={page} showCta={hasCars.stock && hasCars.preOwned} footerLinks={footerLinks}>
      <RentingSeo variant={variant} noindex={hasQuery} seoData={seoData} />
      <MainMenuCollection
        cars={[...cars, ...(preOwnedCars || [])]}
        name={t(`renting.title.${variant}`)}
      />
      <SectionContainer>
        <Titles>
          <Title el="h1" variant="title2" weight="medium">
            {seoData?.title || t(`renting.title.${variant}`)}
          </Title>
          <Typography el="h2" weight="light">
            {t(`renting.subtitle.${variant}`)}
          </Typography>
        </Titles>
        <StyledRow className={show === "desktop" ? "with-filter" : null}>
          {hasFilter && <Filter show={show} setShow={setShow} onUpdate={onFilterUpdate} isDesktop={isDesktop} />}
          <StyledCol>
            {variant !== "flexible" && <Actions show={show} setShow={setShow} isDesktop={isDesktop} hasFilter={hasFilter} />}
            {locale === "es" && variant === "pre-owned" && <PreOwnedBanner />}
            <ListWrapper>
              <Stock setHasCars={setHasStock} hasFilter={hasFilter} cars={cars} ignoreQuery={disableUrlFilters} />
            </ListWrapper>
            {variant === "pre-owned" && preOwnedCars?.length > 0 && (
              <PreOwned hasCars={hasCars.preOwned} setHasPreOwned={setHasPreOwned} hasFilter={hasFilter} />
            )}
            {variant === "pre-owned" && (
              <DynamicSeoSection pageSlug="renting-seminuevos" />
            )}
            {variant === "commercial" && (
              <DynamicSeoSection pageSlug="renting-furgonetas" />
            )}
          </StyledCol>
        </StyledRow>
        <ListLandings landingsList={landingsList} />
      </SectionContainer>
    </DefaultLayout>
  )
}

export default RentingScene
